import { Builder } from '@builder.io/react';
import React from 'react';
import { twMerge } from 'tailwind-merge';

import {
  Button,
  type ButtonProps,
} from '../../storyblocks-design-system/components/atoms/Button/Button';
import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';

const BUTTON_VARIANTS = [
  'primary',
  'secondary',
  'outlined-dark',
  'outlined-light',
  'text',
  'alternate',
];

export interface FooterHookProps {
  headline: string;
  paragraph: string;
  content: string;
  showCallToAction: boolean;
  callToAction: ButtonProps & { text: string };
  shadowVariant: boolean;
  shadowOptions?: {
    shadowColor: string;
    shadowImage: string;
  };
}

const FooterHook = ({
  headline,
  paragraph,
  showCallToAction,
  callToAction,
  shadowVariant,
  shadowOptions,
}: FooterHookProps) => {
  const { shadowColor, shadowImage } = shadowOptions || {};
  const bgClass = twMerge(
    'relative bg-blue-200 px-6 py-12 md:p-12 md:gap-4 lg:p-24 rounded-2xl',
    shadowVariant ? 'bg-forest-200' : 'bg-blue-200'
  );
  return (
    <div className="px-6 py-16 md:px-12 md:py-24 lg:p-24">
      <div className="relative w-[calc(100%-2rem)] left-6">
        {shadowVariant ? (
          <div className="absolute -inset-0">
            <div
              className="absolute w-full h-full -left-6 top-6 rounded-2xl"
              style={{
                backgroundColor: shadowColor,
                backgroundImage: shadowImage
                  ? `url(${shadowImage})`
                  : undefined,
                backgroundSize: shadowImage && 'cover',
              }}
              role="presentation"
            />
          </div>
        ) : null}
        <div className={bgClass}>
          <div className="flex flex-col gap-4 lg:flex-row lg:gap-20">
            <Text
              fontFamily="polysans"
              fontSize="4xl"
              fontWeight="semibold"
              className="m-0"
              asChild
            >
              <h2>{headline}</h2>
            </Text>
            <div>
              <Text fontFamily="polysans" fontSize="lg" fontWeight="regular">
                <span>{paragraph}</span>
              </Text>
              {showCallToAction ? (
                <Button
                  variant={callToAction.variant}
                  className="mt-8"
                  size="large"
                  href={callToAction.href}
                  target={callToAction.target}
                >
                  {callToAction.text}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

Builder.registerComponent(FooterHook, {
  name: 'Footer Hook SB',
  inputs: [
    {
      name: 'headline',
      type: 'string',
      required: true,
      defaultValue: 'Imagine your team with Storyblocks for Business',
    },
    {
      name: 'paragraph',
      type: 'longText',
      required: true,
      defaultValue:
        'Take the first step in leveling up your team’s creative process and get ahead of the competition by requesting a personalized quote today.',
    },
    {
      name: 'shadowVariant',
      type: 'boolean',
      defaultValue: false,
    },
    {
      name: 'shadowOptions',
      type: 'object',
      showIf: (options) => options.get('shadowVariant'),
      defaultValue: {
        shadowColor: 'var(--color-gray-100, #f5f5f5)',
        shadowImage: '',
      },
      subFields: [
        {
          name: 'shadowColor',
          type: 'color',
        },
        {
          name: 'shadowImage',
          type: 'file',
          allowedFileTypes: ['jpg', 'jpeg', 'png', 'webp'],
        },
      ],
    },
    {
      name: 'showCallToAction',
      type: 'boolean',
      defaultValue: true,
    },
    {
      name: 'callToAction',
      type: 'object',
      defaultValue: {
        text: 'Explore Footage',
        href: '/',
        variant: BUTTON_VARIANTS[5],
        size: 'default',
        target: '_self',
        rel: '',
      },
      showIf: (options) => options.get('showCallToAction'),
      subFields: [
        {
          name: 'text',
          type: 'string',
          defaultValue: '',
          required: true,
        },
        {
          name: 'href',
          type: 'string',
          defaultValue: '/',
          required: true,
        },
        {
          name: 'target',
          type: 'string',
          enum: ['_self', '_blank', '_parent', '_top'],
          helperText:
            'For explanation of options, see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/a#attr-target',
        },
        {
          name: 'variant',
          type: 'string',
          defaultValue: BUTTON_VARIANTS[5],
          enum: BUTTON_VARIANTS,
        },
      ],
    },
  ],
});
